/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA
 *
 */

import baseRequest from 'core/fetchRequest';
import request, { getEndpointUrl } from '@/core/apiRequest/apiRequest';

/**
 * @param {string} sessionId - The identifier of the session
 * @param {string} returnUrl
 * @param {boolean} forceDefaultReturnUrl
 * @returns {Promise<Response>}
 */
export const launchDeliveryExecution = (sessionId, returnUrl, forceDefaultReturnUrl = false) =>
    request(getEndpointUrl('launchDeliveryExecution', { sessionId }), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            returnUrl: forceDefaultReturnUrl ? void 0 : returnUrl
        })
    });

/**
 * @param {string} sessionId - The identifier of the session
 * @param {string} returnUrl
 * @param {boolean} forceDefaultReturnUrl
 * @returns {Promise<Response>}
 */
export const launchPublicDeliveryExecution = (sessionId, returnUrl, forceDefaultReturnUrl = false) =>
    baseRequest(getEndpointUrl('launchPublicDeliveryExecution', { sessionId }), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            returnUrl: forceDefaultReturnUrl ? void 0 : returnUrl
        })
    });

/**
 * Review delivery execution
 * @param {string} deliveryExecutionId - identifier of delivery execution
 * @param {string} [itemId] - identifier of item
 * @returns {Promise<Response>}
 */
export const reviewDeliveryExecution = (deliveryExecutionId, itemId) =>
    request(getEndpointUrl('reviewDeliveryExecution', { deliveryExecutionId, itemId }), {}, true);

/**
 * @param {string} sessionId - The identifier of the session
 * @returns {Promise<Response>}
 */
export const launchProctoringMonitoring = sessionId =>
    request(getEndpointUrl('launchProctoringMonitoring', { sessionId }), {});

/**
 * @param {string} user - The login of the user
 * @returns {Promise<Response>}
 */
export const launchUserProctoringMonitoring = user =>
    request(getEndpointUrl('launchUserProctoringMonitoring', { user }), {});

/**
 * @param {string[]} deliveryExecutionIds - identifier(s) of delivery execution(s)
 * @param {object} [options] - claim options
 * @param {object} [options.breadcrumbs] - breadcrumbs
 * @param {string} [options.returnUrl] - return urls
 * @param {boolean} options.isReview
 * @param options.presentationOrder
 * @param options.unscoredFirst
 * @returns {Promise<Response>}
 */
export const launchGrader = (
    deliveryExecutionIds,
    { breadcrumbs, returnUrl, isReview, presentationOrder, unscoredFirst } = {}
) =>
    request(getEndpointUrl('launchGrader'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            deliveryExecutionIds,
            presentationOrder,
            unscoredFirst,
            breadcrumbs,
            returnUrl,
            isReview
        })
    });

/**
 * Launch delivery execution anonymously
 * @param {string} deliveryId
 * @returns {Promise<Response>}
 */
export const previewDeliveryExecution = deliveryId =>
    request(getEndpointUrl('previewDeliveryExecution', { deliveryId }));

export const previewBattery = (batteryId, timezone) =>
    request(getEndpointUrl('previewBattery', { batteryId }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            timezone
        })
    });

export const launchAuthoring = (returnUrl, logoutUrl, loginUrl) =>
    request(getEndpointUrl('launchAuthoring'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            returnUrl,
            logoutUrl,
            loginUrl
        })
    });
